import { Dialog, DialogProps } from "../ui/Dialog"
import { useTranslation } from "react-i18next"
import { isImplicit, StyleReferenceImage } from "../Styles/StyleReferenceImage"
import { StyleResponseDTO } from "@/api/dto/styles"
import { useAtomValue, useSetAtom } from "jotai"
import {
	styleReferencesAtom,
	fetchStyleReferencesAtom,
	displayStyleIdAtom,
} from "@/store/style/style-state"
import { useCallback, useEffect, useMemo, useState } from "react"
import { Spinner } from "../ui/Spinner"
import { Icon } from "../icons/Icon"
import { Button } from "../ui/Button"
import { pushDialog } from "@/store/dialogs-state"
import { ConfirmDialog } from "./ConfirmDialog"
import { StyleInfoRecord, StyleReferenceRecord } from "@/lib/catalog"
import { ScrollBar } from "../ui/ScrollBar"
import { deleteStyleReferences } from "@/lib/catalog/helpers"
import { StyleService } from "@/store/style/style-service"
import clsx from "clsx"
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu"
import { ContentTooltipWrapper } from "../ui/ContentTooltip"
import { useUserQuery } from "@/api/users-api"
import { isStaffPlusRole } from "@/utils/user-role"

type FilterType = "All" | "System" | "Custom"

const ReferenceImagesPanel = ({
	filterType,
	references,
	onReferenceClicked,
	selectedReferences,
}: {
	filterType: FilterType
	references: StyleReferenceRecord[]
	onReferenceClicked?: (imageId: string | undefined) => boolean
	selectedReferences: Set<string>
}) => {
	const filteredReferences = useMemo(() => {
		switch (filterType) {
			case "System":
				return references.filter((item) => isImplicit(item))
			case "Custom":
				return references.filter((item) => !isImplicit(item))
			case "All":
			default:
				return references
		}
	}, [references, filterType])

	const { t } = useTranslation("components")
	const errorMessage =
		filteredReferences.length > 0
			? ""
			: filterType === "System"
				? t("styleReferencesDialog.noSystemReferences")
				: t("styleReferencesDialog.noUserReferences")
	return (
		<div className={clsx("size-full")}>
			{filteredReferences.length > 0 && (
				<div className="scrollarea relative grid h-full min-h-0 auto-rows-min grid-cols-5 gap-2 overflow-y-auto">
					{filteredReferences.map((value) => (
						<StyleReferenceImage
							key={value.imageId}
							reference={value}
							aspectFit
							growToFit
							onSelected={onReferenceClicked}
							isSelected={selectedReferences.has(value.imageId)}
							backgroundBaseColor="base-900"
							showFileName={true}
						/>
					))}
					<ScrollBar visible="hover" />
				</div>
			)}
			{filteredReferences.length === 0 && (
				<div className="flex size-full items-center justify-center text-sm">
					{errorMessage}
				</div>
			)}
		</div>
	)
}

const ContentPanel = (props: {
	style: StyleInfoRecord | StyleResponseDTO
	filterType: FilterType
	references: StyleReferenceRecord[]
	onEnterDeletingMode: (enter: boolean) => void
	deleteReferences: (styleIds: Set<string>) => Promise<void>
	isStaffPlus: boolean
	isSharedStyle: boolean
}) => {
	const { t } = useTranslation("components")
	const [selectedReferences, setSelectedReferences] = useState<Set<string>>(new Set())
	const [inEditingMode, setInEditingMode] = useState<boolean>(false)
	const [isDeleting, setIsDeleting] = useState<boolean>(false)

	const onReferenceClicked = useCallback((imageId: string | undefined) => {
		if (!imageId) {
			return false
		}

		let selected = false

		setSelectedReferences((prev) => {
			const next = new Set(prev)
			if (next.has(imageId)) {
				next.delete(imageId)
			} else {
				next.add(imageId)
				selected = true
			}
			return next
		})

		return selected
	}, [])

	const deleteReferences = async () => {
		if (selectedReferences.size === 0) {
			return
		}
		setIsDeleting(true)
		await props.deleteReferences(new Set([...selectedReferences]))
		setSelectedReferences(new Set())
		setIsDeleting(false)
	}

	const handleDeleteReferences = () => {
		if (selectedReferences.size === props.references.length) {
			pushDialog(ConfirmDialog, {
				title: t("styleReferencesDialog.unableToDeleteAllTitle"),
				message: t("styleReferencesDialog.unableToDeleteAllMessage"),
				secondary: {
					title: t("common:action.ok"),
					handler: () => {},
				},
				trackingViewName: "alert_delete_reference_images",
			})
		} else {
			const sharedWarning =
				props.isStaffPlus && props.isSharedStyle
					? t("styleReferencesDialog.sharedStyleWarning")
					: null
			pushDialog(ConfirmDialog, {
				title: t("styleReferencesDialog.deleteReferencesTitle"),
				message: t("styleReferencesDialog.deleteReferencesMessage"),
				embeddedComponent: sharedWarning ? (
					<div className="mt-2 text-sm font-semibold text-yellow-500">
						{sharedWarning}
					</div>
				) : undefined,
				secondary: { title: t("common:action.cancel") },
				primary: {
					title: t("styleReferencesDialog.ariaDelete"),
					isDestructive: true,
					handler: () => {
						void deleteReferences()
					},
				},
				trackingViewName: "delete_reference_images",
			})
		}
	}

	const renderBottomBar = () => {
		const selectedCount = selectedReferences.size
		const deletionTitle = t("styleReferencesDialog.deleteNSelected", { count: selectedCount })

		return (
			<div className="flex w-full flex-row items-center justify-between pt-4">
				<Button
					trackingLabel="cancel"
					onClick={() => {
						setSelectedReferences(new Set())
						setInEditingMode(false)
						props.onEnterDeletingMode(false)
					}}
					variant="default"
					className={clsx(!inEditingMode && "invisible")}
					disabled={isDeleting}
				>
					{t("common:action.cancel")}
				</Button>

				{inEditingMode && (
					<Button
						trackingLabel="delete"
						onClick={() => {
							handleDeleteReferences()
						}}
						variant="primary"
						disabled={selectedCount === 0 || isDeleting}
					>
						{deletionTitle}
					</Button>
				)}

				{!inEditingMode && (
					<Button
						trackingLabel="delete_button"
						aria-label={t("styleReferencesDialog.ariaDelete")}
						onClick={() => {
							setInEditingMode(true)
							props.onEnterDeletingMode(true)
						}}
						variant="default"
						disabled={props.references.length === 0}
					>
						{t("styleReferencesDialog.editReferences")}
					</Button>
				)}
			</div>
		)
	}

	return (
		<div className="size-full">
			<ReferenceImagesPanel
				filterType={props.filterType}
				references={props.references}
				onReferenceClicked={inEditingMode ? onReferenceClicked : undefined}
				selectedReferences={selectedReferences}
			/>
			{isDeleting && (
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/50">
					<Spinner size={24} />
				</div>
			)}
			<div className="shrink-0">{renderBottomBar()}</div>
		</div>
	)
}

export const StyleReferencesDialog = (
	props: DialogProps & {
		style: StyleResponseDTO | StyleInfoRecord
		fromProject: boolean
		onReferenceDeleted?: (referenceCount: number) => void
	},
) => {
	const { data: user } = useUserQuery()
	const { t } = useTranslation("components")
	const isStaffPlus = isStaffPlusRole(user?.role)
	const isSharedStyle =
		props.style.visibility === "public" || props.style.visibility === "internal"
	const [filterType, setFilterType] = useState<FilterType>("All")
	const [isDeleting, setIsDeleting] = useState<boolean>(false)
	const [referenceDeleted, setReferenceDeleted] = useState<boolean>(false)

	const { data: styleReferences, isLoading: isLoadingReferences } =
		useAtomValue(fetchStyleReferencesAtom)
	const localReferences = useAtomValue(styleReferencesAtom)
	const references = (props.fromProject ? localReferences : styleReferences) ?? []

	const setDisplayStyleId = useSetAtom(displayStyleIdAtom)

	useEffect(() => {
		if (!props.fromProject) {
			setDisplayStyleId(props.style.styleId)
		}
	}, [props.style.styleId, props.fromProject])

	const renderTypeOptions = () => {
		const getText = (filterType: FilterType) => {
			switch (filterType) {
				case "System":
					return t("styleReferencesDialog.systemReferences")
				case "Custom":
					return t("styleReferencesDialog.userReferences")
				case "All":
				default:
					return t("styleReferencesDialog.allTypes")
			}
		}

		const renderMenuItem = (fType: FilterType, info?: string) => {
			return (
				<div className="flex w-full flex-row items-center space-x-2">
					<div className="h-full w-4">
						{filterType === fType && <Icon name="check" size={16} />}
					</div>
					<span className="grow text-sm text-base-300">{getText(fType)}</span>
					{info && (
						<ContentTooltipWrapper
							tooltipDelay={0}
							tooltip={info}
							tooltipProps={{ side: "right", sideOffset: 18 }}
							tooltipMaxWidth={400}
						>
							<Icon name="info" color="gray" size={16} />
						</ContentTooltipWrapper>
					)}
				</div>
			)
		}

		return (
			<DropdownMenu>
				<DropdownMenuTrigger>
					<div className="electron-no-drag flex max-w-[200px] flex-row items-center space-x-1 truncate text-sm font-medium text-base-400 hover:text-base-50">
						{getText(filterType)}
					</div>
				</DropdownMenuTrigger>
				<DropdownMenuContent
					aria-label={t("styleReferencesDialog.ariaStyleReferencesTypeMenu")}
					className="rounded-lg border border-base-700 bg-base-800 p-2"
					side="bottom"
					align="start"
					sideOffset={4}
				>
					<DropdownMenuItem
						trackingLabel="references_all"
						aria-label={t("styleReferencesDialog.showAllReferences")}
						onClick={() => {
							setFilterType("All")
						}}
					>
						{renderMenuItem("All")}
					</DropdownMenuItem>

					<DropdownMenuItem
						trackingLabel="references_system"
						aria-label={t("styleReferencesDialog.showSystemReferences")}
						onClick={() => {
							setFilterType("System")
						}}
					>
						{renderMenuItem("System", t("styleReferencesDialog.systemReferencesInfo"))}
					</DropdownMenuItem>

					<DropdownMenuItem
						trackingLabel="references_custom"
						aria-label={t("styleReferencesDialog.showCustomReferences")}
						onClick={() => {
							setFilterType("Custom")
						}}
					>
						{renderMenuItem("Custom", t("styleReferencesDialog.customReferencesInfo"))}
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		)
	}

	const deleteReferences = useCallback(
		async (imageIds: Set<string>) => {
			if (props.fromProject) {
				await deleteStyleReferences([...imageIds], props.style.styleId)
			} else {
				await StyleService.deleteStyleReferences(props.style.styleId, props.style.version, [
					...imageIds,
				])
			}
			setReferenceDeleted(true)
		},
		[props.fromProject, props.style.styleId, props.style.version],
	)

	const handleOpenChange = (open: boolean) => {
		if (!open) {
			if (referenceDeleted) {
				// update the reference count in style description
				props.onReferenceDeleted?.(references.length)
			}
		}
		props.onOpenChange?.(open)
	}

	return (
		<Dialog
			contentSize="lg"
			{...props}
			canGoBack={false}
			onOpenChange={handleOpenChange}
			trackingViewName={"view_references_dialog"}
		>
			<div className="flex h-full min-h-0 flex-col gap-4 pb-2">
				<div className="shrink-0 text-start text-md font-bold">
					{isDeleting
						? t("styleReferencesDialog.selectImagesToDelete")
						: isLoadingReferences
							? t("styleReferencesDialog.referenceImages")
							: t("styleReferencesDialog.referenceImagesCount", {
									count: references.length,
								})}
				</div>
				<div className="flex shrink-0 flex-row items-center justify-start space-x-8">
					{renderTypeOptions()}
				</div>
				<div className="min-h-0 flex-1 pb-8">
					{isLoadingReferences && (
						<div className="flex size-full items-center justify-center">
							<Spinner size={24} />
						</div>
					)}
					{!isLoadingReferences && (
						<ContentPanel
							style={props.style}
							filterType={filterType}
							onEnterDeletingMode={(value: boolean) => {
								setIsDeleting(value)
							}}
							references={references}
							deleteReferences={deleteReferences}
							isStaffPlus={isStaffPlus}
							isSharedStyle={isSharedStyle}
						/>
					)}
				</div>
			</div>
		</Dialog>
	)
}
